import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { VESSEL_SKIN_ASSET, VesselSkin, parseVesselSkin } from '../shared/utils/vessel-skin';

const GENERIC_BOAT_ICON = '/assets/common_icons/boat_other_user.png';
const LEGACY_BOW_OFFSET_DEG = 30;

/**
 * Which hull each vessel is drawn with, keyed by vessel id — port of Flutter's
 * `VesselSkinStore`. The backend owns the value (`boat_skin` on the vessel, also sent on
 * `community/live-positions/`, `trip/live/feed` and `vessel/`); this is a read-through cache so
 * the map can answer "what skin is vessel X?" synchronously while rendering.
 *
 * Unknown ≠ sportfish: another captain's boat we have not read yet must not be drawn as a
 * confident sportfish, so callers use `forVesselOrNull` and fall back to the generic boat.
 */
@Injectable({ providedIn: 'root' })
export class VesselSkinStore {
  private readonly byVesselId = new Map<string, VesselSkin>();
  private readonly changedSubject = new Subject<void>();
  /** Emits whenever a cached skin actually changed (so markers can re-draw their art). */
  readonly changed$ = this.changedSubject.asObservable();

  /** Art for another captain's boat: its own hull when known, else the generic boat. */
  iconSrcFor(vesselId: string | null | undefined): string {
    const skin = this.forVesselOrNull(vesselId);
    return skin ? VESSEL_SKIN_ASSET[skin] : GENERIC_BOAT_ICON;
  }

  /**
   * Degrees to ADD to a heading so the art's bow lands on it. The skins are drawn bow-up (0); the
   * legacy PNG's bow is 30° off north, the same constant the app uses.
   */
  headingOffsetFor(vesselId: string | null | undefined): number {
    return this.forVesselOrNull(vesselId) ? 0 : LEGACY_BOW_OFFSET_DEG;
  }

  forVesselOrNull(vesselId: string | null | undefined): VesselSkin | null {
    const id = vesselId?.trim();
    return id ? this.byVesselId.get(id) ?? null : null;
  }

  /**
   * Record a payload's `boat_skin`. A null/blank value is "the payload says nothing", never "this
   * vessel is a sportfish" — otherwise a list that omits the field would overwrite a real choice.
   */
  remember(vesselId: string | null | undefined, rawSkin: unknown): void {
    const id = vesselId?.trim();
    const skin = parseVesselSkin(rawSkin);
    if (!id || !skin || this.byVesselId.get(id) === skin) return;
    this.byVesselId.set(id, skin);
    this.changedSubject.next();
  }
}
